'use client';

import { useState, useCallback, useRef } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export const useVerticalLines = () => {
  const [verticalLines, setVerticalLines] = useState<VerticalLine[]>([]);
  const [loadingLines, setLoadingLines] = useState<Set<string>>(new Set());
  const nextTNumber = useRef<number>(1);
  const nextMNumber = useRef<number>(1);

  const addVerticalLine = useCallback(
    (
      timestamp: Date | number,
      values: Record<string, number>,
      xValue?: string | number,
      customLabel?: string,
      customColor?: string,
      isHarmonic?: boolean,
      isFirstHarmonic?: boolean,
      chartId?: string,
      sectionKey?: string,
      markerType?: 'system' | 'user',
      originalPrefix?: string,
      groupId?: string
    ) => {
      const id = `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const colors = ['#f87171', '#86efac', '#93c5fd', '#fde047', '#c4b5fd', '#67e8f9'];

      setVerticalLines((currentLines) => {
        // Create completely new mutable array
        const freshArray = [];

        // Copy existing lines to new array
        if (currentLines && Array.isArray(currentLines)) {
          for (let i = 0; i < currentLines.length; i++) {
            freshArray.push({...currentLines[i]});
          }
        }

        const color = customColor || colors[freshArray.length % colors.length];
        let label = customLabel || `L${freshArray.length + 1}`;

        // Parse custom line format: "U0|unicorn-events|👤 username"
        let isCustomLine = false;
        let extractedUsername = '';
        let extractedGroupId = groupId;
        let extractedGroupIcon = '';

        if (customLabel && customLabel.includes('|')) {
          const parts = customLabel.split('|');
          if (parts.length >= 3) {
            label = parts[0]; // Clean label (e.g., "U0")
            extractedGroupId = parts[1]; // Group ID (e.g., "unicorn-events")
            const userPart = parts[2]; // Username part (e.g., "👤 username")
            if (userPart) {
              extractedUsername = userPart.replace('👤 ', '').trim();
            }
            isCustomLine = true;
            // Set icon based on group
            if (extractedGroupId === 'unicorn-events') {
              extractedGroupIcon = '🦄';
            } else if (extractedGroupId === 'teddy-events') {
              extractedGroupIcon = '🧸';
            }
          }
        }

        if (markerType === 'user' && !customLabel) {
          let prefix = 'M';
          if (timestamp instanceof Date) {
            const year = timestamp.getFullYear();
            const time = timestamp.getTime();
            if (year >= 1970 && time >= 100000000000) {
              prefix = 'T';
            }
          }
          if (typeof xValue === 'number' || typeof timestamp === 'number') {
            prefix = 'M';
          }
          if (prefix === 'T') {
            label = `T${nextTNumber.current}`;
            nextTNumber.current += 1;
          } else {
            label = `M${nextMNumber.current}`;
            nextMNumber.current += 1;
          }
        }

        const newLine: any = {
          id,
          timestamp,
          label,
          values: {...(values || {})},
          color,
          isHarmonic: !!isHarmonic,
          isFirstHarmonic: !!isFirstHarmonic,
          chartId,
          sectionKey,
          markerType,
          originalPrefix,
          groupId: extractedGroupId,
        };

        // Add custom line fields if this is a custom line
        if (isCustomLine) {
          newLine.isCustomLine = true;
          newLine.username = extractedUsername;
          newLine.groupIcon = extractedGroupIcon;
          newLine.source = 'user';
          newLine._systemGroupId = extractedGroupId;
        }

        freshArray.push(newLine);
        return freshArray;
      });
    },
    []
  );

  const removeVerticalLine = useCallback((id: string) => {
    setVerticalLines((prev) => {
      const wasBaseline = prev.length > 0 && prev[0].id === id;
      const updatedLines = prev.filter((line) => line.id !== id);

      if (wasBaseline && updatedLines.length > 0) {
        return updatedLines.sort((a, b) => {
          const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : a.timestamp;
          const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : b.timestamp;
          return aTime - bTime;
        });
      }

      return updatedLines;
    });
  }, []);

  const clearAllLines = useCallback(() => {
    setVerticalLines([]);
    // Reset marker counters so T1, T2, etc. restart from 1
    nextTNumber.current = 1;
    nextMNumber.current = 1;
  }, []);

  const updateVerticalLineValues = useCallback((lineId: string, newValues: Record<string, number>) => {
    setVerticalLines((prev) =>
      prev.map((line) => (line.id === lineId ? { ...line, values: { ...line.values, ...newValues } } : line))
    );
  }, []);

  const updateAllVerticalLineValues = useCallback(
    (getValuesAtTimestamp: (timestamp: Date | number, existingValues: Record<string, number>) => Record<string, number>) => {
      setVerticalLines((prev) =>
        prev.map((line) => ({
          ...line,
          values: getValuesAtTimestamp(line.timestamp, line.values || {}),
        }))
      );
    },
    []
  );

  const updateVerticalLine = useCallback((lineId: string, updates: Partial<VerticalLine>) => {
    setVerticalLines((prev) => prev.map((line) => (line.id === lineId ? { ...line, ...updates } : line)));
  }, []);

  return {
    verticalLines,
    loadingLines,
    addVerticalLine,
    removeVerticalLine,
    clearAllLines,
    updateVerticalLineValues,
    updateAllVerticalLineValues,
    updateVerticalLine,
  };
};
